import { Tooltip } from '@epam/promo';

import { ReactComponent as ChatGPTImage } from 'assets/icons/chatGPT.svg';
import { useAISuggestion, useLeaveAIAssistant } from 'hooks';
import { MIN_STRING_LENGTH, TOOLTIP_DELAY } from './constants';
import { AISuggestionBlock } from './AISuggestionBlock';
import { AISuggestionWrapperProps } from './AISuggestionWrapper.model';
import css from './AISuggestionWrapper.module.scss';

export const AISuggestionWrapper = ({
  children,
  options,
  value,
  setValue,
}: AISuggestionWrapperProps) => {
  const AISuggestionModule = useAISuggestion();
  const { AIButtonRef, isAIButtonShown, isAISuggestionBlockShown } =
    AISuggestionModule.data;
  const {
    onInputBlur,
    onInputFocus,
    onAIButtonBlur,
    onAIButtonClick,
    onAISuggestionBlockClose,
  } = AISuggestionModule.handlers;
  const leaveAIAssistant = useLeaveAIAssistant();

  const onApply = (newValue: string) => {
    setValue(newValue, {
      markAsDirty: true,
      shouldValidate: true,
    });
    onAISuggestionBlockClose();
  };

  const onCloseAIAssistant = (
    isCloseImmediately: boolean,
    suggestionAmount: number,
  ) => {
    if (isCloseImmediately && !suggestionAmount) {
      onAISuggestionBlockClose();

      return;
    }

    leaveAIAssistant({
      onSuccess: onAISuggestionBlockClose,
      onAbort: () => {},
    });
  };

  const isAIButtonActive =
    value.length >= MIN_STRING_LENGTH && !isAISuggestionBlockShown;

  return (
    <>
      <div className={css.suggestion__container}>
        {children({
          onBlur: onInputBlur,
          onFocus: onInputFocus,
          isAISuggestionBlockShown,
        })}
        {isAIButtonShown && (
          <Tooltip
            ref={AIButtonRef}
            content="With AI assistance, you can rephrase your text."
            openDelay={isAIButtonActive ? TOOLTIP_DELAY : 0}
            placement="top">
            <button
              className={`${css.suggestion__image} ${
                isAIButtonActive ? css.active : ''
              }`}
              type="button"
              onBlur={onAIButtonBlur}
              onClick={onAIButtonClick}>
              <ChatGPTImage
                stroke={
                  isAIButtonActive
                    ? 'var(--uui-primary-50)'
                    : 'var(--uui-neutral-40)'
                }
              />
            </button>
          </Tooltip>
        )}
      </div>
      {isAISuggestionBlockShown && (
        <AISuggestionBlock
          options={options}
          text={value}
          onApply={onApply}
          onClose={onCloseAIAssistant}
        />
      )}
    </>
  );
};
